const SFX_ENABLED_KEY = 'bedrock-sfx-enabled'

type OscType = OscillatorType

const getAudioContext = () => {
  if (typeof window === 'undefined') return null
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
  if (!AudioCtx) return null
  return new AudioCtx()
}

class SoundFxEngine {
  private context: AudioContext | null = null
  private enabled = true
  private ready = false
  private lastHoverAt = 0

  constructor() {
    if (typeof window !== 'undefined') {
      const persisted = window.localStorage.getItem(SFX_ENABLED_KEY)
      this.enabled = persisted !== '0'
    }
  }

  setEnabled(value: boolean) {
    this.enabled = value
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SFX_ENABLED_KEY, value ? '1' : '0')
    }
  }

  isEnabled() {
    return this.enabled
  }

  init() {
    if (this.ready) return
    this.context = getAudioContext()
    this.ready = true
  }

  async resume() {
    this.init()
    if (!this.context) return
    if (this.context.state === 'suspended') {
      await this.context.resume()
    }
  }

  private playTone(options: {
    freq: number
    durationMs: number
    type?: OscType
    volume?: number
    attackMs?: number
    releaseMs?: number
    endFreq?: number
  }) {
    if (!this.enabled) return
    this.init()
    if (!this.context) return

    const now = this.context.currentTime
    const duration = Math.max(0.01, options.durationMs / 1000)
    const attack = Math.max(0.001, (options.attackMs ?? 6) / 1000)
    const release = Math.max(0.001, (options.releaseMs ?? 55) / 1000)
    const volume = Math.max(0, Math.min(1, options.volume ?? 0.05))
    const type = options.type ?? 'triangle'

    const osc = this.context.createOscillator()
    const gain = this.context.createGain()
    const filter = this.context.createBiquadFilter()

    filter.type = 'lowpass'
    filter.frequency.value = Math.max(700, options.freq * 4)
    filter.Q.value = 0.3

    osc.type = type
    osc.frequency.setValueAtTime(options.freq, now)
    if (options.endFreq && options.endFreq !== options.freq) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, options.endFreq), now + duration)
    }

    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), now + attack)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration + release)

    osc.connect(filter)
    filter.connect(gain)
    gain.connect(this.context.destination)

    osc.start(now)
    osc.stop(now + duration + release + 0.02)
  }

  click() {
    this.playTone({ freq: 540, endFreq: 410, durationMs: 42, volume: 0.045, type: 'triangle', releaseMs: 70 })
  }

  hover() {
    const now = performance.now()
    if (now - this.lastHoverAt < 110) return
    this.lastHoverAt = now
    this.playTone({ freq: 780, endFreq: 720, durationMs: 26, volume: 0.016, type: 'sine', releaseMs: 35 })
  }

  toggle(on: boolean) {
    this.playTone({
      freq: on ? 610 : 390,
      endFreq: on ? 740 : 320,
      durationMs: 46,
      volume: 0.04,
      type: on ? 'triangle' : 'sawtooth',
      releaseMs: 75,
    })
  }

  navigate() {
    this.playTone({ freq: 420, endFreq: 590, durationMs: 55, volume: 0.028, type: 'sine', releaseMs: 95 })
  }
}

export const soundFx = new SoundFxEngine()

